"use client";

import * as React from "react";
import { PropsWithChildren, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";

import { proxyQueryParams } from "./__internal__/QueryParamsProxy";
import { IQueryParamsProps, IRawQueryParamsProps } from "./IQueryParamsProps";
import { parseQueryParams } from "./parseQueryParams";
import { QueryParamsOf } from "./QueryParams";

/**
 * Creates a queryParams type with partially-parsed ("raw") query params that are
 * equivalent to the fully-parsed values anyway.
 */
export function withQueryParams<
  T extends IQueryParamsProps<Q, R>,
  Q extends QueryParamsOf<Q>,
  R extends { [K in keyof R]?: string } = object,
>(
  Component: React.ComponentType<T>
): React.ComponentType<Omit<T, "queryParams"> & RouteComponentProps<R>>;

/**
 * Creates a queryParams type with partially-parsed ("raw") query params.
 * @example
 * ```
 * interface IProps = RouteRawQueryParamsProps<"/retailer/settings/payment-methods">;
 * class BaseMyComponent extends React.Component<IProps>;
 * export const MyComponent = withQueryParams(BaseMyComponent);
 * ```
 */
export function withQueryParams<
  T extends IRawQueryParamsProps<Q, R>,
  Q extends QueryParamsOf<Q>,
  R extends { [K in keyof R]?: string } = object,
>(
  Component: React.ComponentType<T>
): React.ComponentType<Omit<T, "queryParams"> & RouteComponentProps<R>>;

/**
 * @deprecated use `useQueryParams` instead
 * Creates a queryParams type with strongly-typed parsed query params, using the given parser.
 * @example
 * ```
 * import { QueryParameters, PathParameters } from "@faire/web-api--source/routes/www/retailer/settings/payment-methods";
 * interface IProps = IQueryParamsProps<QueryParameters, PathParameters>;
 * class BaseMyComponent extends React.Component<IProps>;
 * export const MyComponent = withQueryParams(BaseMyComponent, Parameters.parse);
 * ```
 */
export function withQueryParams<
  T extends IQueryParamsProps<Q, R>,
  Q extends object,
  R extends { [K in keyof R]?: string } = object,
>(
  Component: React.ComponentType<T>,
  parsingFunction: (search?: string) => Q
): React.ComponentType<Omit<T, "queryParams"> & RouteComponentProps<R>>;

export function withQueryParams<
  T extends IQueryParamsProps<Q, R>,
  Q extends QueryParamsOf<Q>,
  R extends { [K in keyof R]?: string } = object,
>(Component: React.ComponentType<T>, parsingFunction?: (search?: string) => Q) {
  return function (
    props: PropsWithChildren<
      Omit<T, keyof IQueryParamsProps<Q, R>> & RouteComponentProps<R>
    >
  ) {
    const { location } = props;
    const queryParams = useMemo(() => {
      return parsingFunction
        ? proxyQueryParams(parsingFunction(location.search))
        : parseQueryParams(location.search);
    }, [location.search]);
    return <Component {...(props as T)} queryParams={queryParams} />;
  };
}
